/**
 * Portal v2 protection (`/portal/protection`).
 *
 * Three read-only surfaces on one page rather than three sidebar entries — the
 * portal's value is being simpler than the dashboard, and DNS drift, transport
 * security and impersonation are all "is something happening to my domain that
 * I did not do".
 *
 * Deliberately no rescan control for lookalikes: scans spend a shared
 * certspotter free-tier budget and stay MSP-initiated. The page says so rather
 * than leaving a customer hunting for a button that is not there.
 *
 * TLS and lookalikes are gated on the MSP's plan and 403 when it lacks them;
 * each section renders its own unavailable state so one missing feature does
 * not blank the page.
 */
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { History, Lock, ShieldQuestion } from "lucide-react";
import { Skeleton } from "@ddmarc/ui";
import { portalApi } from "@/lib/portal-api";
import { usePortalScope } from "@/lib/portal-scope-context";
import { CardV2, KpiTile, SectionHeader, StatPill } from "@/components/primitives";

const riskTone = (risk: string): "danger" | "warn" | "neutral" =>
  risk === "high" ? "danger" : risk === "medium" ? "warn" : "neutral";

const statusTone = (status: string): "good" | "warn" | "danger" =>
  status === "pass" ? "good" : status === "warning" ? "warn" : "danger";

/** Shown when the partner's plan does not include a capability. */
function Unavailable({ what }: { what: string }) {
  return (
    <div className="flex items-center gap-2 py-6 text-[12.5px] text-muted-foreground">
      <Lock className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{what} is not included in your current service. Contact your provider to enable it.</span>
    </div>
  );
}

export function PortalProtectionV2Page() {
  const { selectedDomainId } = usePortalScope();

  const history = useQuery({
    queryKey: ["portal", "dns-history", selectedDomainId],
    queryFn: () =>
      portalApi.getDnsHistory({ domain_id: selectedDomainId, changes_only: true, size: 25 }),
  });

  // A 403 here means the MSP's plan lacks the feature — expected, not an error
  // worth retrying.
  const tls = useQuery({
    queryKey: ["portal", "tls-reports", selectedDomainId],
    queryFn: () => portalApi.getTlsReports({ domain_id: selectedDomainId }),
    retry: false,
  });

  const lookalikes = useQuery({
    queryKey: ["portal", "lookalikes", selectedDomainId],
    queryFn: () => portalApi.getLookalikes({ domain_id: selectedDomainId }),
    retry: false,
  });

  return (
    <div className="space-y-3.5">
      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <ShieldQuestion className="h-5 w-5" />
            Protection
          </span>
        }
        subtitle="Changes to your DNS, transport security, and domains impersonating yours."
      />

      {/* --- DNS change history --------------------------------------------- */}
      <CardV2
        title="Recent DNS changes"
        subtitle="Only checks where a record actually changed."
        bodyClassName="p-0"
      >
        {history.isLoading ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : !history.data?.items.length ? (
          <div className="flex flex-col items-center gap-2 py-10 text-center">
            <History className="h-8 w-8 text-[var(--v2-fg-faint)]" />
            <p className="text-[12.5px] text-muted-foreground">
              No DNS changes detected in this period.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {history.data.items.map((entry) => (
              <div
                key={entry.id}
                className="grid grid-cols-[1.2fr_2fr_0.7fr_0.8fr] items-center gap-3 px-4 py-[var(--v2-row-py,0.625rem)] text-[12.5px]"
              >
                <span className="truncate font-mono-data text-foreground">{entry.domain}</span>
                <span className="truncate text-muted-foreground">
                  {entry.changes_summary ?? "Record changed"}
                </span>
                <span>
                  <StatPill tone={statusTone(entry.overall_status)}>{entry.overall_status}</StatPill>
                </span>
                <span className="text-right text-muted-foreground">
                  {format(new Date(entry.checked_at), "MMM d, yyyy")}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardV2>

      {/* --- TLS reporting --------------------------------------------------- */}
      <CardV2
        title="Transport security"
        subtitle="Whether mail sent to your domains travelled over encrypted connections."
      >
        {tls.isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : tls.isError ? (
          <Unavailable what="TLS reporting" />
        ) : !tls.data?.total ? (
          <p className="py-6 text-[12.5px] text-muted-foreground">
            No TLS reports received yet. Providers send these once TLS-RPT is published.
          </p>
        ) : (
          <div className="grid gap-3 md:grid-cols-3">
            <KpiTile
              label="Successful"
              value={tls.data.total_successful.toLocaleString()}
              hint="Encrypted deliveries"
            />
            <KpiTile
              label="Failed"
              value={tls.data.total_failed.toLocaleString()}
              hint="Could not negotiate TLS"
            />
            <KpiTile
              label="Success rate"
              value={
                <StatPill tone={tls.data.success_rate >= 99 ? "good" : "warn"}>
                  {tls.data.success_rate.toFixed(1)}%
                </StatPill>
              }
              hint={`${tls.data.total} report${tls.data.total === 1 ? "" : "s"}`}
            />
          </div>
        )}
      </CardV2>

      {/* --- Lookalike domains ----------------------------------------------- */}
      <CardV2
        title="Lookalike domains"
        subtitle="Registered domains resembling yours, riskiest first."
        bodyClassName={lookalikes.data?.items.length ? "p-0" : undefined}
      >
        {lookalikes.isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : lookalikes.isError ? (
          <Unavailable what="Lookalike domain detection" />
        ) : !lookalikes.data?.items.length ? (
          <p className="py-6 text-[12.5px] text-muted-foreground">
            No lookalike domains are currently flagged for your domains.
          </p>
        ) : (
          <>
            <div className="divide-y divide-border">
              <div className="grid grid-cols-[1.6fr_0.6fr_1.4fr_0.9fr] gap-3 px-4 py-2.5 text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--v2-fg-subtle)]">
                <span>Domain</span>
                <span>Risk</span>
                <span>Signals</span>
                <span className="text-right">First seen</span>
              </div>
              {lookalikes.data.items.map((item) => {
                // Only the signals that are actually true — an empty list reads
                // as "registered but inert", which is the honest summary.
                const signals = [
                  item.has_a && "resolves",
                  item.has_mx && "accepts mail",
                  item.has_cert && "has certificate",
                  item.serves_http && "serves a site",
                  item.is_parked && "parked",
                ].filter(Boolean) as string[];

                return (
                  <div
                    key={item.id}
                    className="grid grid-cols-[1.6fr_0.6fr_1.4fr_0.9fr] items-center gap-3 px-4 py-[var(--v2-row-py,0.625rem)] text-[12.5px]"
                  >
                    <span className="truncate font-mono-data text-foreground">
                      {item.candidate_domain}
                    </span>
                    <span>
                      <StatPill tone={riskTone(item.risk)}>{item.risk}</StatPill>
                    </span>
                    <span className="truncate text-muted-foreground">
                      {signals.length ? signals.join(" · ") : "registered only"}
                    </span>
                    <span className="text-right text-muted-foreground">
                      {format(new Date(item.first_detected_at), "MMM d, yyyy")}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="px-4 py-3 text-[11.5px] text-muted-foreground">
              Detection runs on a schedule managed by your provider. To investigate or dismiss a
              finding, contact them.
            </p>
          </>
        )}
      </CardV2>
    </div>
  );
}
